import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane, faSearch, faCheck, faPlus, faTimes, faEye, faEyeSlash, faMagic, faCommentDots, faPen,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { Button, Form, Modal, Spinner } from '@themesberg/react-bootstrap';
import {
  collection, query, getDocs, doc, getDoc,
  addDoc, updateDoc, serverTimestamp, deleteDoc, where, setDoc,
  writeBatch, Timestamp, orderBy, limit
} from 'firebase/firestore';
import { db, auth } from '../../config/firebase';

const PRESETS = [
  { id: 'merhaba', label: 'Merhaba 👋' },
  { id: 'nasilsin', label: 'Nasılsın?' },
  { id: 'tanisalim', label: 'Tanışalım mı?' }
];

const SEQUENCE_KEY = 'jigogo-bulk-reply-sequence';
const UNDO_KEY = 'jigogo-unpaid-collapse-undo';
const PRESET_PHRASE = {
  merhaba: 'merhaba',
  nasilsin: 'nasılsın',
  tanisalim: 'tanışalım mı'
};

function foldTr(value) {
  return String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c');
}

function corePhrase(text) {
  const stripped = String(text || '')
    .replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}]/gu, ' ');
  return foldTr(stripped.replace(/[^0-9A-Za-zÇĞİÖŞÜçğıöşü\s]/g, ' ').replace(/\s+/g, ' ').trim());
}

function matchesFilter(text, preset, extraQuery) {
  const core = corePhrase(text);
  if (!core) return false;
  const extra = corePhrase(extraQuery || '');
  if (preset === 'manual') return !!extra && core.includes(extra);
  const target = corePhrase(PRESET_PHRASE[preset] || '');
  if (!target || core !== target) return false;
  if (extra && !core.includes(extra)) return false;
  return true;
}

function toDate(value) {
  if (!value) return new Date(0);
  if (typeof value.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date(0) : date;
}

async function findBuyers(userIds) {
  const bought = new Set();
  const size = 10;
  for (let i = 0; i < userIds.length; i += size) {
    const chunk = userIds.slice(i, i + size);
    if (chunk.length === 0) continue;
    const [credits, vips] = await Promise.all([
      getDocs(query(collection(db, 'purchases'), where('userId', 'in', chunk))),
      getDocs(query(collection(db, 'vipPackagePayments'), where('userId', 'in', chunk)))
    ]);
    credits.forEach((item) => {
      const id = item.data().userId;
      if (id) bought.add(id);
    });
    vips.forEach((item) => {
      const id = item.data().userId;
      if (id) bought.add(id);
    });
  }
  return bought;
}

function queueRow(data, messageText, messageId, date) {
  return {
    id: messageId,
    roomId: data.chatRoomId,
    memberId: data.senderId,
    memberName: data.senderName || 'Üye',
    memberAvatar: data.senderAvatar || null,
    memberAge: data.senderAge || null,
    memberCity: data.senderCity || '',
    botId: data.receiverId,
    botName: data.receiverName || 'Onaylı profil',
    botAvatar: data.receiverAvatar || null,
    botAge: data.receiverAge || null,
    message: messageText || '',
    messageId: data.messageId || null,
    messageType: data.messageType || 'text',
    date
  };
}

async function loadQueueRooms() {
  const snap = await getDocs(collection(db, 'unansweredMessages'));
  const byRoom = new Map();
  snap.forEach((item) => {
    const data = item.data();
    if (data.parked === true) return;
    const roomId = data.chatRoomId;
    if (!roomId || !data.senderId || !data.receiverId) return;
    const date = toDate(data.timestamp || data.createdAt);
    const prev = byRoom.get(roomId);
    if (!prev || date > prev.date) {
      byRoom.set(roomId, { id: item.id, data, date });
    }
  });
  return [...byRoom.values()];
}

function roomsToRows(pool, preset, extraQuery) {
  return pool
    .filter((room) => matchesFilter(room.data.message, preset, extraQuery))
    .sort((a, b) => a.date - b.date)
    .map((room) => queueRow(room.data, room.data.message, room.id, room.date));
}

async function sendOneReply(row, text) {
  const messageData = {
    senderId: row.botId,
    receiverId: row.memberId,
    message: text,
    timestamp: serverTimestamp(),
    type: 'text'
  };
  await addDoc(collection(db, 'chatRooms', row.roomId, 'messages'), messageData);

  const roomRef = doc(db, 'chatRooms', row.roomId);
  const roomPatch = {
    participantUids: [row.botId, row.memberId],
    lastMessage: text,
    lastMessageAt: serverTimestamp(),
    lastMessageType: 'text',
    lastMessageSenderId: row.botId,
    ignoredAt: null
  };
  try {
    await updateDoc(roomRef, roomPatch);
  } catch (err) {
    await setDoc(roomRef, roomPatch, { merge: true });
  }

  const unanswered = await getDocs(query(
    collection(db, 'unansweredMessages'),
    where('chatRoomId', '==', row.roomId)
  ));
  await Promise.all(unanswered.docs.map((item) => deleteDoc(item.ref)));

  await setDoc(doc(db, 'answeredMessages', row.roomId), {
    chatRoomId: row.roomId,
    senderId: row.memberId,
    senderName: row.memberName,
    senderAvatar: row.memberAvatar || null,
    receiverId: row.botId,
    receiverName: row.botName,
    receiverAvatar: row.botAvatar || null,
    message: text,
    answeredAt: serverTimestamp(),
    adminId: auth.currentUser ? auth.currentUser.uid : null
  });
}

function serializeValue(value) {
  if (value && typeof value.toDate === 'function') return { __ts: value.toDate().getTime() };
  return value;
}

function serializeDoc(item) {
  const raw = item.data();
  const data = {};
  Object.keys(raw).forEach((key) => {
    data[key] = serializeValue(raw[key]);
  });
  return { id: item.id, data };
}

function reviveValue(value) {
  if (value && typeof value.__ts === 'number') return Timestamp.fromMillis(value.__ts);
  return value;
}

function reviveDoc(data) {
  const out = {};
  Object.keys(data || {}).forEach((key) => {
    out[key] = reviveValue(data[key]);
  });
  return out;
}

export function collapseUndoAvailable() {
  try {
    const saved = JSON.parse(localStorage.getItem(UNDO_KEY) || 'null');
    return !!(saved && Array.isArray(saved.docs) && saved.docs.length > 0);
  } catch (err) {
    return false;
  }
}

export async function collapseUnpaidRooms() {
  const snap = await getDocs(collection(db, 'unansweredMessages'));
  const latestByRoom = new Map();
  snap.forEach((item) => {
    const data = item.data();
    if (data.parked === true) return;
    const roomId = data.chatRoomId;
    const senderId = String(data.senderId || '').trim();
    const receiverId = String(data.receiverId || '').trim();
    if (!roomId || !senderId || !receiverId) return;
    const date = toDate(data.timestamp || data.createdAt);
    const prev = latestByRoom.get(roomId);
    if (!prev || date > prev.date) {
      latestByRoom.set(roomId, {
        roomId,
        senderId,
        receiverId,
        senderName: data.senderName || 'Bilinmeyen',
        message: String(data.message || '').slice(0, 40),
        date
      });
    }
  });

  const bySender = new Map();
  latestByRoom.forEach((room) => {
    const list = bySender.get(room.senderId) || [];
    list.push(room);
    bySender.set(room.senderId, list);
  });

  const multi = [];
  bySender.forEach((list, senderId) => {
    const receivers = new Set(list.map((room) => room.receiverId));
    if (receivers.size < 2) return;
    multi.push({ senderId, list, receivers: receivers.size });
  });

  const buyers = await findBuyers(multi.map((row) => row.senderId));
  const dropRoomIds = new Set();
  const samples = [];
  const memberIds = new Set();
  multi.forEach((row) => {
    if (buyers.has(row.senderId)) return;
    const ordered = row.list.slice().sort((a, b) => b.date - a.date);
    const drop = ordered.slice(1);
    drop.forEach((room) => dropRoomIds.add(room.roomId));
    if (drop.length) memberIds.add(row.senderId);
    if (samples.length < 12 && drop.length) {
      samples.push({
        senderId: row.senderId,
        name: ordered[0].senderName,
        conversations: row.receivers,
        keptMessage: ordered[0].message
      });
    }
  });

  const docs = snap.docs
    .filter((item) => dropRoomIds.has(item.data().chatRoomId))
    .map(serializeDoc);

  if (docs.length) {
    for (let i = 0; i < docs.length; i += 400) {
      const batch = writeBatch(db);
      docs.slice(i, i + 400).forEach((item) => {
        batch.delete(doc(db, 'unansweredMessages', item.id));
      });
      await batch.commit();
    }
    localStorage.setItem(UNDO_KEY, JSON.stringify({
      docs,
      members: memberIds.size,
      ignored: dropRoomIds.size
    }));
  }

  return {
    scanned: latestByRoom.size,
    members: memberIds.size,
    ignored: dropRoomIds.size,
    canUndo: docs.length > 0,
    ignoredRoomIds: [...dropRoomIds],
    samples
  };
}

export async function undoCollapseUnpaid() {
  const saved = JSON.parse(localStorage.getItem(UNDO_KEY) || 'null');
  if (!saved || !Array.isArray(saved.docs) || saved.docs.length === 0) {
    return { restored: 0 };
  }
  const current = await getDocs(collection(db, 'unansweredMessages'));
  const liveRooms = new Set();
  const liveIds = new Set();
  current.forEach((item) => {
    liveIds.add(item.id);
    const roomId = item.data().chatRoomId;
    if (roomId) liveRooms.add(roomId);
  });
  const pending = saved.docs.filter((item) => {
    const roomId = item.data && item.data.chatRoomId;
    return item.id && !liveIds.has(item.id) && !(roomId && liveRooms.has(roomId));
  });
  for (let i = 0; i < pending.length; i += 400) {
    const batch = writeBatch(db);
    pending.slice(i, i + 400).forEach((item) => {
      batch.set(doc(db, 'unansweredMessages', item.id), reviveDoc(item.data));
    });
    await batch.commit();
  }
  localStorage.removeItem(UNDO_KEY);
  return { restored: pending.length };
}

async function commitInChunks(ids, patch) {
  for (let i = 0; i < ids.length; i += 400) {
    const batch = writeBatch(db);
    ids.slice(i, i + 400).forEach((id) => {
      batch.update(doc(db, 'unansweredMessages', id), patch);
    });
    await batch.commit();
  }
}

function waitingStatsFrom(docs) {
  const people = new Set();
  docs.forEach((row) => {
    const senderId = String(row.senderId || '').trim();
    if (senderId) people.add(senderId);
  });
  return { messages: docs.length, people: people.size };
}

export async function parkWaitingRoom() {
  const snap = await getDocs(collection(db, 'unansweredMessages'));
  const active = [];
  const alreadyParked = [];
  snap.forEach((item) => {
    const data = item.data();
    const row = {
      id: item.id,
      senderId: String(data.senderId || '').trim(),
      roomId: data.chatRoomId || '',
      name: data.senderName || 'Bilinmeyen',
      message: String(data.message || '').slice(0, 40),
      date: toDate(data.timestamp || data.createdAt)
    };
    if (data.parked === true) alreadyParked.push(row);
    else if (row.senderId) active.push(row);
  });

  const bySender = new Map();
  active.forEach((row) => {
    const list = bySender.get(row.senderId) || [];
    list.push(row);
    bySender.set(row.senderId, list);
  });

  const candidates = [];
  bySender.forEach((list, senderId) => {
    if (list.length < 3) return;
    candidates.push({ senderId, list });
  });

  const buyers = await findBuyers(candidates.map((row) => row.senderId));
  const toPark = [];
  let skippedNoPurchase = 0;
  candidates.forEach((row) => {
    if (!buyers.has(row.senderId)) {
      skippedNoPurchase += 1;
      return;
    }
    const ordered = row.list.slice().sort((a, b) => b.date - a.date);
    toPark.push(...ordered.slice(1));
  });

  if (toPark.length) await commitInChunks(toPark.map((row) => row.id), { parked: true });

  const parkedIds = new Set(toPark.map((row) => row.id));
  const stillVisibleRooms = new Set();
  active.forEach((row) => {
    if (!parkedIds.has(row.id) && row.roomId) stillVisibleRooms.add(row.roomId);
  });
  const parkedRoomIds = [...new Set(
    toPark.map((row) => row.roomId).filter((roomId) => roomId && !stillVisibleRooms.has(roomId))
  )];

  return {
    scanned: active.length,
    members: new Set(toPark.map((row) => row.senderId)).size,
    parked: toPark.length,
    skippedNoPurchase,
    parkedRoomIds,
    stats: waitingStatsFrom([...alreadyParked, ...toPark])
  };
}

export async function releaseWaitingOne() {
  const snap = await getDocs(collection(db, 'unansweredMessages'));
  const oldestBySender = new Map();
  const parked = [];
  snap.forEach((item) => {
    const data = item.data();
    if (data.parked !== true) return;
    const row = {
      id: item.id,
      senderId: String(data.senderId || '').trim(),
      date: toDate(data.timestamp || data.createdAt)
    };
    parked.push(row);
    if (!row.senderId) return;
    const current = oldestBySender.get(row.senderId);
    if (!current || row.date < current.date) oldestBySender.set(row.senderId, row);
  });
  const release = [...oldestBySender.values()];
  if (release.length) await commitInChunks(release.map((row) => row.id), { parked: false });
  const releasedIds = new Set(release.map((row) => row.id));
  const remaining = parked.filter((row) => !releasedIds.has(row.id));
  return { released: release.length, stats: waitingStatsFrom(remaining) };
}

export async function releaseWaitingAll() {
  const snap = await getDocs(collection(db, 'unansweredMessages'));
  const ids = [];
  snap.forEach((item) => {
    if (item.data().parked === true) ids.push(item.id);
  });
  if (ids.length) await commitInChunks(ids, { parked: false });
  return { released: ids.length, stats: { messages: 0, people: 0 } };
}

async function loadUserMap(ids) {
  const users = new Map();
  const size = 20;
  for (let i = 0; i < ids.length; i += size) {
    const chunk = ids.slice(i, i + size);
    const snaps = await Promise.all(chunk.map((id) => getDoc(doc(db, 'users', id))));
    snaps.forEach((snap, index) => {
      if (snap.exists()) users.set(chunk[index], snap.data());
    });
  }
  return users;
}

export async function resetUnpaidQueueCredits() {
  const snap = await getDocs(collection(db, 'unansweredMessages'));
  const senderIds = new Set();
  snap.forEach((item) => {
    const data = item.data();
    if (data.parked === true) return;
    const senderId = String(data.senderId || '').trim();
    if (senderId) senderIds.add(senderId);
  });
  const ids = [...senderIds];
  if (!ids.length) {
    return { listed: 0, reset: 0, skippedPaid: 0, skippedZero: 0, skippedBot: 0, resetIds: [] };
  }

  const [buyers, users] = await Promise.all([findBuyers(ids), loadUserMap(ids)]);
  const toReset = [];
  let skippedPaid = 0;
  let skippedZero = 0;
  let skippedBot = 0;
  ids.forEach((id) => {
    const user = users.get(id);
    if (!user || user.isApproved || user.isFake || user.isTestUser || user.isAdmin) {
      skippedBot += 1;
      return;
    }
    if (buyers.has(id) || Number(user.purchasedPackagesCount || 0) > 0) {
      skippedPaid += 1;
      return;
    }
    if (Number(user.credits || 0) <= 0) {
      skippedZero += 1;
      return;
    }
    toReset.push(id);
  });

  for (let i = 0; i < toReset.length; i += 400) {
    const batch = writeBatch(db);
    toReset.slice(i, i + 400).forEach((id) => {
      batch.update(doc(db, 'users', id), { credits: 0 });
    });
    await batch.commit();
  }

  return {
    listed: ids.length,
    reset: toReset.length,
    skippedPaid,
    skippedZero,
    skippedBot,
    resetIds: toReset
  };
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function delayFor(mode) {
  if (mode === '0') return 0;
  if (mode === '1') return 1000;
  if (mode === '3') return 3000;
  return 1000 + Math.floor(Math.random() * 2000);
}

function locative(city) {
  if (!city || city === '-') return '';
  const cleanCity = String(city).trim();
  if (!cleanCity) return '';
  const lastVowelMatch = cleanCity.match(/[aeıioöuü]/gi);
  const lastVowel = lastVowelMatch ? lastVowelMatch.pop().toLocaleLowerCase('tr-TR') : 'a';
  const lastChar = cleanCity.slice(-1).toLocaleLowerCase('tr-TR');
  const isHard = 'fstkçşhp'.includes(lastChar);
  if ('aıou'.includes(lastVowel)) return cleanCity + (isHard ? "'ta" : "'da");
  return cleanCity + (isHard ? "'te" : "'de");
}

const profileCache = new Map();

async function loadProfile(uid) {
  if (!uid) return null;
  if (profileCache.has(uid)) return profileCache.get(uid);
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    const data = snap.exists() ? snap.data() : null;
    profileCache.set(uid, data);
    return data;
  } catch (err) {
    profileCache.set(uid, null);
    return null;
  }
}

async function enrichRows(list) {
  const need = new Set();
  list.forEach((row) => {
    if (!row.memberCity || !row.memberName || row.memberName === 'Üye') need.add(row.memberId);
    if (!row.botName || row.botName === 'Onaylı profil' || row.botAge == null) need.add(row.botId);
  });
  const ids = [...need].filter(Boolean);
  const size = 8;
  for (let i = 0; i < ids.length; i += size) {
    await Promise.all(ids.slice(i, i + size).map((id) => loadProfile(id)));
  }
  return list.map((row) => {
    const member = profileCache.get(row.memberId);
    const bot = profileCache.get(row.botId);
    return {
      ...row,
      memberName: (row.memberName && row.memberName !== 'Üye') ? row.memberName : (member?.name || row.memberName),
      memberCity: row.memberCity || member?.city || '',
      memberAge: row.memberAge || member?.age || null,
      memberAvatar: row.memberAvatar || member?.profileImageUrl || member?.avatar || null,
      botName: (row.botName && row.botName !== 'Onaylı profil') ? row.botName : (bot?.name || row.botName),
      botAge: row.botAge || bot?.age || null,
      botAvatar: row.botAvatar || bot?.profileImageUrl || bot?.avatar || null
    };
  });
}

function firstName(fullName) {
  const first = String(fullName || '').trim().split(/\s+/)[0] || '';
  if (!first) return 'Üye';
  return first.charAt(0).toLocaleUpperCase('tr-TR') + first.slice(1).toLocaleLowerCase('tr-TR');
}

function fillPreview(template, row) {
  if (!template) return '';
  const full = String(row?.memberName || '').trim() || 'Üye';
  const name = firstName(full);
  const city = row?.memberCity || '';
  return String(template)
    .replace(/\{\{\s*first_uye_name\s*\}\}/gi, name)
    .replace(/\{\{\s*uye_ad\s*\}\}/gi, full)
    .replace(/\{\{\s*uye_sehir_da\s*\}\}/gi, locative(city) || city)
    .replace(/\{\{\s*uye_sehir(_adi)?\s*\}\}/gi, city)
    .replace(/\{\{\s*onayli_ad\s*\}\}/gi, row?.botName || '')
    .replace(/\{\{\s*onayli_yas\s*\}\}/gi, row?.botAge != null ? String(row.botAge) : '');
}

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Europe/Istanbul'
  });
}

function Face({ src, name }) {
  if (src) {
    return <img src={src} alt="" className="brf-face" />;
  }
  return <span className="brf-face brf-face-fallback">{(name || 'U').charAt(0).toUpperCase()}</span>;
}

function rowTime(row) {
  const time = new Date(row?.date || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function duplicateRowIds(list) {
  const byMember = new Map();
  list.forEach((row) => {
    const member = String(row.memberId || '');
    if (!member) return;
    if (!byMember.has(member)) byMember.set(member, []);
    byMember.get(member).push(row);
  });
  const extra = new Set();
  byMember.forEach((memberRows) => {
    const rooms = new Map();
    memberRows.forEach((row) => {
      const room = String(row.roomId || row.id);
      const time = rowTime(row);
      const prev = rooms.get(room);
      if (prev == null || time > prev) rooms.set(room, time);
    });
    if (rooms.size < 2) return;
    let keepRoom = null;
    let keepTime = -1;
    rooms.forEach((time, room) => {
      if (time > keepTime) {
        keepTime = time;
        keepRoom = room;
      }
    });
    memberRows.forEach((row) => {
      if (String(row.roomId || row.id) !== keepRoom) extra.add(row.id);
    });
  });
  return extra;
}

function selectionFor(list, collapse) {
  const ids = new Set(list.map((row) => row.id));
  if (collapse) duplicateRowIds(list).forEach((id) => ids.delete(id));
  return ids;
}

function loadSequence() {
  try {
    const saved = JSON.parse(localStorage.getItem(SEQUENCE_KEY) || '[]');
    if (Array.isArray(saved) && saved.length > 0) return saved.map((line) => String(line));
  } catch (err) {
    return ['', '', ''];
  }
  return ['', '', ''];
}

export default function BulkReplyModal({ show, onHide, quickMessages, onDone }) {
  const [audience, setAudience] = useState('unpaid');
  const [preset, setPreset] = useState('merhaba');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(false);
  const [replyMode, setReplyMode] = useState('plain');
  const [pickedId, setPickedId] = useState(null);
  const [manualText, setManualText] = useState('');
  const [sequence, setSequence] = useState(loadSequence);
  const [intervalMode, setIntervalMode] = useState('0');
  const [collapseSameMember, setCollapseSameMember] = useState(true);
  const [sending, setSending] = useState(false);
  const [censoringId, setCensoringId] = useState(null);
  const [censoredIds, setCensoredIds] = useState(() => new Set());
  const [ignoringId, setIgnoringId] = useState(null);
  const [peek, setPeek] = useState(null);
  const [peekMessages, setPeekMessages] = useState([]);
  const [peekLoading, setPeekLoading] = useState(false);
  const [peekText, setPeekText] = useState('');
  const [peekSending, setPeekSending] = useState(false);
  const [peekError, setPeekError] = useState(null);
  const peekListRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const cancelRef = useRef(false);
  const queryRef = useRef(null);
  const collapseRef = useRef(true);
  collapseRef.current = collapseSameMember;
  const roomsRef = useRef(null);
  const buyersRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    localStorage.setItem(SEQUENCE_KEY, JSON.stringify(sequence));
  }, [sequence]);

  useEffect(() => {
    if (!show) {
      roomsRef.current = null;
      buyersRef.current = null;
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setResult(null);

    const run = async () => {
      if (!roomsRef.current) roomsRef.current = await loadQueueRooms();
      const rooms = roomsRef.current;
      if (!buyersRef.current) {
        buyersRef.current = await findBuyers([...new Set(rooms.map((room) => room.data.senderId))]);
      }
      const buyers = buyersRef.current;
      const pool = rooms.filter((room) => {
        const paid = buyers.has(room.data.senderId);
        return audience === 'paid' ? paid : !paid;
      });

      const list = roomsToRows(pool, preset, debouncedQuery);

      if (!alive) return;
      const capped = list.length > 500;
      const visible = capped ? list.slice(0, 500) : list;
      const enriched = await enrichRows(visible);
      if (!alive) return;
      setRows(enriched);
      setTruncated(capped);
      setSelected(selectionFor(enriched, collapseRef.current));
      setCensoredIds(new Set());
      setPeek(null);
      setExpanded(false);
    };

    run().catch(() => {
      if (!alive) return;
      setRows([]);
      setSelected(new Set());
    }).finally(() => {
      if (alive) setLoading(false);
    });

    return () => { alive = false; };
  }, [show, preset, debouncedQuery, audience]);

  const plainMessages = useMemo(
    () => (quickMessages || []).filter((msg) => !msg.isSmart && (msg.content || msg.text)),
    [quickMessages]
  );
  const smartMessages = useMemo(
    () => (quickMessages || []).filter((msg) => msg.isSmart && (msg.content || msg.text)),
    [quickMessages]
  );
  const activeList = replyMode === 'smart' ? smartMessages : plainMessages;
  const picked = activeList.find((msg) => msg.id === pickedId) || null;

  const templates = useMemo(() => {
    if (audience === 'paid') {
      return sequence.map((line) => line.trim()).filter(Boolean);
    }
    if (replyMode === 'manual') return manualText.trim() ? [manualText.trim()] : [];
    const body = (picked && (picked.content || picked.text)) || '';
    return body.trim() ? [body.trim()] : [];
  }, [audience, sequence, replyMode, manualText, picked]);

  const duplicateIds = useMemo(() => duplicateRowIds(rows), [rows]);

  const orderedRows = useMemo(() => {
    if (!collapseSameMember || duplicateIds.size === 0) return rows;
    const extras = [];
    const rest = [];
    rows.forEach((row) => {
      if (duplicateIds.has(row.id)) extras.push(row);
      else rest.push(row);
    });
    return [...extras, ...rest];
  }, [rows, collapseSameMember, duplicateIds]);

  const selectedRows = useMemo(
    () => orderedRows.filter((row) => selected.has(row.id)),
    [orderedRows, selected]
  );

  const assignment = useMemo(() => {
    const map = new Map();
    if (templates.length === 0) return map;
    selectedRows.forEach((row, index) => {
      const slot = index % templates.length;
      map.set(row.id, { n: slot + 1, text: templates[slot] });
    });
    return map;
  }, [selectedRows, templates]);

  const visibleRows = expanded ? orderedRows : orderedRows.slice(0, 8);
  const hiddenCount = Math.max(0, rows.length - visibleRows.length);
  const previewRow = selectedRows[0] || rows[0];
  const previewText = previewRow && templates[0] ? fillPreview(templates[0], previewRow) : '';

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((row) => row.id)));
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const dropRoom = (roomId) => {
    setRows((prev) => prev.filter((item) => item.roomId !== roomId));
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((item) => {
        if (item.roomId === roomId) next.delete(item.id);
      });
      return next;
    });
    if (peek && peek.roomId === roomId) setPeek(null);
    if (roomsRef.current) {
      roomsRef.current = roomsRef.current.filter((room) => room.data.chatRoomId !== roomId);
    }
  };

  const censorRow = async (row) => {
    if (!row || censoredIds.has(row.id) || censoringId) return;
    const type = row.messageType || 'text';
    const text = String(row.message || '');
    if (type !== 'text' || !text) return;
    if (/^\*+$/.test(text)) {
      setCensoredIds((prev) => new Set(prev).add(row.id));
      return;
    }
    setCensoringId(row.id);
    try {
      let messageId = row.messageId;
      if (!messageId && row.roomId) {
        const listed = await getDocs(query(
          collection(db, 'chatRooms', row.roomId, 'messages'),
          orderBy('timestamp', 'desc'),
          limit(40)
        ));
        const found = listed.docs.find((item) => {
          const data = item.data();
          return data.senderId === row.memberId && data.type !== 'system' && data.message === text;
        });
        messageId = found && found.id;
      }
      if (!row.roomId || !messageId) {
        alert('Mesaj sansürlenirken bir hata oluştu.');
        return;
      }
      const stars = '*'.repeat(text.length);
      await updateDoc(doc(db, 'chatRooms', row.roomId, 'messages', messageId), { message: stars });
      try {
        await updateDoc(doc(db, 'unansweredMessages', row.id), { message: stars });
      } catch (err) {
        console.error('Cevapsiz kayit metni guncellenemedi:', err);
      }
      setRows((prev) => prev.map((item) => (item.id === row.id ? { ...item, message: stars } : item)));
      setPeekMessages((prev) => prev.map((item) => (
        item.id === messageId ? { ...item, message: stars } : item
      )));
      setCensoredIds((prev) => new Set(prev).add(row.id));
    } catch (err) {
      alert('Mesaj sansürlenirken bir hata oluştu.');
    } finally {
      setCensoringId(null);
    }
  };

  const ignoreRow = async (row) => {
    if (!row?.roomId || ignoringId || sending) return;
    setIgnoringId(row.id);
    try {
      const listed = await getDocs(query(
        collection(db, 'unansweredMessages'),
        where('chatRoomId', '==', row.roomId)
      ));
      await Promise.all(listed.docs.map((item) => deleteDoc(item.ref)));
      dropRoom(row.roomId);
    } catch (err) {
      alert('Mesaj görmezden gelinemedi.');
    } finally {
      setIgnoringId(null);
    }
  };

  const openPeek = async (row) => {
    if (!row?.roomId) return;
    setPeek(row);
    setPeekText('');
    setPeekError(null);
    setPeekLoading(true);
    setPeekMessages([]);
    try {
      const listed = await getDocs(query(
        collection(db, 'chatRooms', row.roomId, 'messages'),
        orderBy('timestamp', 'asc'),
        limit(80)
      ));
      setPeekMessages(listed.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (err) {
      setPeekError('Sohbet açılamadı.');
    } finally {
      setPeekLoading(false);
    }
  };

  const sendPeek = async () => {
    const text = peekText.trim();
    if (!peek || !text || peekSending) return;
    setPeekSending(true);
    setPeekError(null);
    try {
      await sendOneReply(peek, fillPreview(text, peek));
      dropRoom(peek.roomId);
      setPeekText('');
      if (onDone) onDone();
    } catch (err) {
      setPeekError('Cevap gönderilemedi.');
    } finally {
      setPeekSending(false);
    }
  };

  useEffect(() => {
    const box = peekListRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [peekMessages, peekLoading]);

  const addSequenceFrom = (msg) => {
    const body = (msg && (msg.content || msg.text)) || '';
    if (!body) return;
    setSequence((prev) => [...prev, body]);
  };

  const send = async () => {
    const queue = selectedRows.slice();
    if (sending || queue.length === 0 || templates.length === 0) return;
    cancelRef.current = false;
    setSending(true);
    setResult(null);
    let sent = 0;
    let failed = 0;
    for (let i = 0; i < queue.length; i += 1) {
      if (cancelRef.current) break;
      const row = queue[i];
      const plan = assignment.get(row.id);
      setProgress({ current: i + 1, total: queue.length, name: row.memberName });
      try {
        await sendOneReply(row, fillPreview(plan.text, row), i === 0 || queue[i - 1].roomId !== row.roomId);
        sent += 1;
        const roomFinished = !queue.slice(i + 1).some((item) => item.roomId === row.roomId);
        setRows((prev) => prev.filter((item) => item.id !== row.id && !(roomFinished && item.roomId === row.roomId)));
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          if (roomFinished) {
            queue.forEach((item) => {
              if (item.roomId === row.roomId) next.delete(item.id);
            });
          }
          return next;
        });
        if (roomFinished && roomsRef.current) {
          roomsRef.current = roomsRef.current.filter((room) => room.data.chatRoomId !== row.roomId);
        }
      } catch (err) {
        failed += 1;
      }
      if (i < queue.length - 1 && !cancelRef.current) {
        await sleep(delayFor(intervalMode));
      }
    }
    setSending(false);
    setProgress(null);
    const stopped = cancelRef.current;
    setResult(
      `${sent} mesaj gönderildi` +
      (failed ? `, ${failed} gönderilemedi` : '') +
      (stopped ? '. Gönderim durduruldu.' : '.')
    );
    if (sent > 0 && onDone) onDone();
  };

  return (
    <Modal show={show} onHide={sending ? undefined : onHide} centered dialogClassName="brf-dialog" contentClassName="brf-content">
      <style>{`
        .brf-dialog { max-width: 1560px; width: calc(100vw - 1rem); }
        .brf-content { border: 0; border-radius: 18px; overflow: hidden; }
        .brf-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 18px 22px 8px; }
        .brf-title { display: flex; gap: 10px; align-items: flex-start; }
        .brf-title h5 { margin: 0; font-weight: 700; }
        .brf-title p { margin: 2px 0 0; color: #6c757d; font-size: 13px; }
        .brf-body { padding: 8px 22px 16px; background: #f7f9fc; max-height: calc(100vh - 150px); overflow: auto; }
        .brf-body-split { display: flex; gap: 12px; overflow: hidden; }
        .brf-main { min-width: 0; }
        .brf-body-split .brf-main { flex: 1; overflow: auto; }
        .brf-table th.brf-censor, .brf-table td.brf-censor { width: 64px; text-align: center; white-space: nowrap; }
        .brf-table th.brf-ignore, .brf-table td.brf-ignore { width: 108px; text-align: center; white-space: nowrap; }
        .brf-peek { width: 400px; flex: 0 0 400px; background: #fff; border: 1px solid #e6ebf2; border-radius: 14px; display: flex; flex-direction: column; min-height: 0; }
        .brf-peek-head { display: flex; justify-content: space-between; gap: 8px; align-items: flex-start; padding: 12px; border-bottom: 1px solid #eef2f6; }
        .brf-peek-head strong { display: block; }
        .brf-peek-head span { color: #6c757d; font-size: 12px; }
        .brf-peek-msgs { flex: 1; overflow: auto; padding: 12px; background: #f7f9fc; }
        .brf-bubble { max-width: 88%; padding: 7px 9px; border-radius: 10px; margin-bottom: 6px; font-size: 13px; white-space: pre-wrap; word-break: break-word; }
        .brf-bubble.them { background: #fff; border: 1px solid #e6ebf2; }
        .brf-bubble.me { background: #2f6fed; color: #fff; margin-left: auto; }
        .brf-peek-form { display: flex; gap: 8px; padding: 10px; border-top: 1px solid #eef2f6; }
        .brf-open { border: 0; background: transparent; padding: 0; text-align: left; color: inherit; cursor: pointer; }
        .brf-open:hover { color: #2f6fed; }
        .brf-audience { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
        .brf-audience button { text-align: left; border-radius: 12px; border: 1px solid #d7dee8; background: #fff; padding: 10px 12px; }
        .brf-audience button strong { display: block; }
        .brf-audience button span { color: #6c757d; font-size: 12px; }
        .brf-audience button.on { border-color: #2f6fed; background: #eef4ff; box-shadow: inset 0 0 0 1px #2f6fed; }
        .brf-card { background: #fff; border: 1px solid #e6ebf2; border-radius: 14px; padding: 14px; margin-bottom: 12px; }
        .brf-step { display: flex; justify-content: space-between; gap: 12px; align-items: center; margin-bottom: 10px; }
        .brf-step h6 { margin: 0; font-size: 15px; }
        .brf-step p { margin: 2px 0 0; color: #6c757d; font-size: 12px; }
        .brf-num { width: 22px; height: 22px; border-radius: 50%; background: #2f6fed; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; margin-right: 8px; }
        .brf-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .brf-chip { border: 1px solid #d7dee8; background: #fff; border-radius: 999px; padding: 6px 12px; font-size: 13px; font-weight: 600; }
        .brf-chip.on { background: #2f6fed; color: #fff; border-color: #2f6fed; }
        .brf-count { background: #e8f7ee; color: #157347; border-radius: 12px; padding: 8px 12px; font-size: 13px; min-width: 180px; }
        .brf-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .brf-table th { text-align: left; color: #6c757d; font-weight: 600; font-size: 12px; padding: 6px; border-bottom: 1px solid #eef2f6; }
        .brf-table td { padding: 8px 6px; border-bottom: 1px solid #f2f4f8; vertical-align: middle; }
        .brf-person { display: flex; align-items: center; gap: 8px; }
        .brf-face { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
        .brf-face-fallback { display: inline-flex; align-items: center; justify-content: center; background: #d7dee8; color: #334; font-weight: 700; }
        .brf-age { color: #6c757d; font-weight: 500; margin-left: 4px; }
        .brf-more { width: 100%; border: 0; background: #f3f6fb; color: #2f6fed; border-radius: 10px; padding: 8px; margin-top: 8px; font-weight: 600; }
        .brf-modes { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
        .brf-mode { border: 1px solid #d7dee8; background: #fff; border-radius: 10px; padding: 8px 12px; font-weight: 600; }
        .brf-mode.on { border-color: #2f6fed; color: #2f6fed; background: #eef4ff; }
        .brf-seq { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
        .brf-seq span { width: 28px; height: 28px; border-radius: 8px; background: #eef4ff; color: #2f6fed; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; flex: 0 0 auto; }
        .brf-foot { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 22px 16px; background: #fff; border-top: 1px solid #e6ebf2; }
        .brf-preview { color: #6c757d; font-size: 12px; }
        .brf-slot { display: inline-block; min-width: 22px; text-align: center; background: #eef4ff; color: #2f6fed; border-radius: 6px; font-weight: 700; font-size: 12px; padding: 2px 4px; }
        .brf-warn { background: #fff8e8; }
        .brf-bang { color: #b45309; margin-left: 6px; }
        .brf-collapse { display: flex; align-items: flex-start; gap: 8px; margin-top: 10px; font-size: 13px; }
        .brf-collapse small { display: block; color: #6c757d; font-weight: 400; }
      `}</style>
      <div className="brf-head">
        <div className="brf-title">
          <FontAwesomeIcon icon={faPaperPlane} className="text-primary mt-1" />
          <div>
            <h5>Filtrele ve Toplu Cevapla</h5>
            <p>Aynı mesajı gönderen bekleyen sohbetleri süzün ve tek işlemle yanıtlayın.</p>
          </div>
        </div>
        <Button variant="link" className="text-secondary p-0" onClick={onHide} disabled={sending}>
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </div>
      <div className={`brf-body ${peek ? 'brf-body-split' : ''}`}>
        <div className="brf-main">
        <div className="brf-audience">
          <button type="button" className={audience === 'unpaid' ? 'on' : ''} onClick={() => setAudience('unpaid')} disabled={sending}>
            <strong>Satın almamış</strong>
            <span>Bekleyen sohbetlere aynı cevap gider</span>
          </button>
          <button type="button" className={audience === 'paid' ? 'on' : ''} onClick={() => setAudience('paid')} disabled={sending}>
            <strong>Satın almış</strong>
            <span>Mesaj sırası zamana göre dağıtılır, bitince başa döner</span>
          </button>
        </div>

        <div className="brf-card">
          <div className="brf-step">
            <div>
              <h6><span className="brf-num">1</span>Mesaj filtresi</h6>
              <p>Hangi mesajı yazan üyeler gelsin?</p>
            </div>
            <div className="brf-count">
              <strong>{loading ? '...' : rows.length} konuşma bulundu</strong>
              <div>{preset === 'manual' ? (query || 'Manuel arama') : PRESETS.find((item) => item.id === preset)?.label}</div>
            </div>
          </div>
          <div className="brf-chips mb-2">
            {PRESETS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`brf-chip ${preset === item.id ? 'on' : ''}`}
                onClick={() => setPreset(item.id)}
                disabled={sending}
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              className={`brf-chip ${preset === 'manual' ? 'on' : ''}`}
              onClick={() => {
                setPreset('manual');
                setTimeout(() => queryRef.current?.focus(), 0);
              }}
              disabled={sending}
            >
              <FontAwesomeIcon icon={faPen} className="me-1" />
              Mesaj içeriği manuel gir
            </button>
          </div>
          <div className="position-relative">
            <FontAwesomeIcon icon={faSearch} className="position-absolute text-muted" style={{ left: 12, top: 11 }} />
            <Form.Control
              ref={queryRef}
              value={query}
              placeholder={preset === 'manual' ? 'Aranacak mesaj metnini yazın' : 'İsterseniz metnin içinde de arayın'}
              onChange={(e) => setQuery(e.target.value)}
              style={{ paddingLeft: 34 }}
              disabled={sending}
            />
          </div>
          <Form.Check
            className="brf-collapse"
            type="checkbox"
            id="brf-collapse-same"
            checked={collapseSameMember}
            disabled={sending}
            onChange={(e) => {
              const on = e.target.checked;
              setCollapseSameMember(on);
              setSelected((prev) => {
                const next = new Set(prev);
                duplicateIds.forEach((id) => {
                  if (on) next.delete(id);
                  else next.add(id);
                });
                return next;
              });
            }}
            label={(
              <span>
                Aynı üyeyi 1 mesaja düşür
                <small>
                  Bu kümede aynı üye birden fazla sohbetteyse fazlalık üstte, seçimsiz ve ünlemli durur.
                  {collapseSameMember && duplicateIds.size > 0 ? ` ${duplicateIds.size} sohbet seçilmedi.` : ''}
                </small>
              </span>
            )}
          />
          {truncated && <div className="text-warning small mt-2">Liste 500 kayıtla sınırlandı. İlk 500 kayıt gösteriliyor.</div>}
        </div>

        <div className="brf-card">
          <div className="brf-step">
            <div>
              <h6><span className="brf-num">2</span>Filtrelenen konuşmalar</h6>
              <p>
                {audience === 'paid'
                  ? 'Her sohbet odası bir kez listelenir. Mesaj sütununda o odanın son mesajı vardır.'
                  : 'Satın almamış ve hâlâ cevap bekleyen sohbetler. Her oda bir kez, son mesajıyla.'}
              </p>
            </div>
            <Form.Check
              type="checkbox"
              checked={rows.length > 0 && selected.size === rows.length}
              onChange={toggleAll}
              label={`${selected.size}/${rows.length} seçili`}
              disabled={sending || rows.length === 0}
            />
          </div>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /> Aranıyor...</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-muted py-4">Bu filtreye uyan bekleyen mesaj yok.</div>
          ) : (
            <>
              <table className="brf-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }} />
                    <th>Gönderen</th>
                    <th>Alıcı</th>
                    <th>Son mesaj</th>
                    <th className="brf-censor">Sansür</th>
                    <th className="brf-ignore">Görmezden gel</th>
                    <th>Tarih</th>
                    {audience === 'paid' && <th>Sıra</th>}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const plan = assignment.get(row.id);
                    const warned = collapseSameMember && duplicateIds.has(row.id);
                    return (
                      <tr key={row.id} className={warned ? 'brf-warn' : ''}>
                        <td>
                          <Form.Check
                            checked={selected.has(row.id)}
                            onChange={() => toggleOne(row.id)}
                            disabled={sending}
                          />
                        </td>
                        <td>
                          <div className="brf-person">
                            <Face src={row.memberAvatar} name={row.memberName} />
                            <div>
                              <strong>{row.memberName}</strong>
                              {row.memberAge ? <span className="brf-age">{row.memberAge}</span> : null}
                              {warned && (
                                <span className="brf-bang" title="Aynı üye bu filtrede birden fazla sohbette. Seçilmedi; iki kez mesaj gitmesin.">
                                  <FontAwesomeIcon icon={faExclamationTriangle} />
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="brf-person">
                            <Face src={row.botAvatar} name={row.botName} />
                            <div>
                              <strong>{row.botName}</strong>
                              {row.botAge ? <span className="brf-age">{row.botAge}</span> : null}
                            </div>
                          </div>
                        </td>
                        <td>
                          <button type="button" className="brf-open" title="Sohbeti burada aç" onClick={() => openPeek(row)}>
                            {row.message}
                          </button>
                        </td>
                        <td className="brf-censor">
                          {censoringId === row.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <Form.Check
                              className="mb-0 d-inline-block"
                              checked={censoredIds.has(row.id)}
                              disabled={sending || !!censoringId}
                              onChange={(e) => {
                                if (e.target.checked) censorRow(row);
                              }}
                              title="Metin aynı uzunlukta yıldıza döner. Üyeye bildirim gitmez. Fotoğraf ve ses satırında işlem yapmaz."
                            />
                          )}
                        </td>
                        <td className="brf-ignore">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={sending || ignoringId === row.id || peekSending}
                            onClick={() => ignoreRow(row)}
                            title="Sohbet silinmez. Satır listeden düşer."
                          >
                            {ignoringId === row.id ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faEyeSlash} className="me-1" />}
                            Geç
                          </Button>
                        </td>
                        <td className="text-muted">{formatTime(row.date)}</td>
                        {audience === 'paid' && (
                          <td title={plan ? plan.text : ''}>
                            {plan ? <span className="brf-slot">{plan.n}</span> : '—'}
                          </td>
                        )}
                        <td>
                          <Button variant="link" size="sm" className="p-0" title="Sohbeti burada aç" onClick={() => openPeek(row)}>
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {hiddenCount > 0 && (
                <button type="button" className="brf-more" onClick={() => setExpanded(true)}>
                  +{hiddenCount} konuşma daha
                </button>
              )}
            </>
          )}
        </div>

        <div className="brf-card">
          <div className="brf-step">
            <div>
              <h6><span className="brf-num">3</span>{audience === 'paid' ? 'Mesaj sırası' : 'Cevap'}</h6>
              <p>
                {audience === 'paid'
                  ? 'Her sohbet odasına bir cevap gider. 5 metin ve 80 sohbet varsa 6. sohbet yine 1. metni alır.'
                  : 'Seçilen her sohbete aynı cevap, o sohbetin onaylı hesabından gider.'}
              </p>
            </div>
            <Form.Select
              value={intervalMode}
              onChange={(e) => setIntervalMode(e.target.value)}
              style={{ maxWidth: 180 }}
              disabled={sending}
            >
              <option value="0">Ara vermeden</option>
              <option value="1">1 sn ara</option>
              <option value="1-3">1–3 sn ara</option>
              <option value="3">3 sn ara</option>
            </Form.Select>
          </div>

          {audience === 'unpaid' ? (
            <>
              <div className="brf-modes">
                <button type="button" className={`brf-mode ${replyMode === 'plain' ? 'on' : ''}`} onClick={() => setReplyMode('plain')}>
                  <FontAwesomeIcon icon={faCommentDots} className="me-1" /> Hızlı mesajlar
                </button>
                <button type="button" className={`brf-mode ${replyMode === 'smart' ? 'on' : ''}`} onClick={() => setReplyMode('smart')}>
                  <FontAwesomeIcon icon={faMagic} className="me-1" /> Akıllı mesajlar
                </button>
                <button type="button" className={`brf-mode ${replyMode === 'manual' ? 'on' : ''}`} onClick={() => setReplyMode('manual')}>
                  <FontAwesomeIcon icon={faPen} className="me-1" /> Manuel
                </button>
              </div>
              {replyMode === 'manual' ? (
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={manualText}
                  placeholder="Merhaba {{first_uye_name}}, {{uye_sehir_da}} mı yaşıyorsun?"
                  onChange={(e) => setManualText(e.target.value)}
                  disabled={sending}
                />
              ) : (
                <div className="brf-chips">
                  {activeList.length === 0 && <span className="text-muted small">Bu grupta hazır mesaj yok.</span>}
                  {activeList.map((msg) => (
                    <button
                      key={msg.id}
                      type="button"
                      className={`brf-chip ${pickedId === msg.id ? 'on' : ''}`}
                      onClick={() => setPickedId(msg.id)}
                      disabled={sending}
                    >
                      {pickedId === msg.id && <FontAwesomeIcon icon={faCheck} className="me-1" />}
                      {msg.title || msg.content || msg.text}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="d-flex gap-2 flex-wrap mb-2">
                <Button size="sm" variant="outline-primary" onClick={() => setSequence((prev) => [...prev, ''])} disabled={sending}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" /> Satır ekle
                </Button>
                <Form.Select
                  style={{ maxWidth: 280 }}
                  defaultValue=""
                  onChange={(e) => {
                    const msg = (quickMessages || []).find((item) => item.id === e.target.value);
                    addSequenceFrom(msg);
                    e.target.value = '';
                  }}
                  disabled={sending}
                >
                  <option value="">Hazır mesajdan satır ekle</option>
                  {(quickMessages || []).map((msg) => (
                    <option key={msg.id} value={msg.id}>{msg.isSmart ? 'Akıllı: ' : ''}{msg.title || msg.content}</option>
                  ))}
                </Form.Select>
              </div>
              {sequence.map((line, index) => (
                <div className="brf-seq" key={`seq-${index}`}>
                  <span>{index + 1}</span>
                  <Form.Control
                    value={line}
                    placeholder="Merhaba {{first_uye_name}}"
                    onChange={(e) => {
                      const value = e.target.value;
                      setSequence((prev) => prev.map((item, i) => (i === index ? value : item)));
                    }}
                    disabled={sending}
                  />
                  <Button
                    variant="link"
                    className="text-danger p-0"
                    disabled={sending || sequence.length === 1}
                    onClick={() => setSequence((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </div>
              ))}
            </>
          )}
          <div className="text-muted small mt-2">
            {'{{first_uye_name}}'} ilk isim, {'{{uye_sehir_da}}'} şehrin bulunma hali. Her üyede kendi bilgisiyle dolar.
            {previewText ? ` Örnek: ${previewText}` : ''}
          </div>
        </div>
        </div>
        {peek && (
          <aside className="brf-peek">
            <div className="brf-peek-head">
              <div>
                <strong>{peek.memberName} → {peek.botName}</strong>
                <span>Cevap onaylı hesaptan gider. Gönderince satır listeden düşer.</span>
              </div>
              <Button variant="link" className="text-secondary p-0" onClick={() => setPeek(null)} disabled={peekSending}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
            <div className="brf-peek-msgs" ref={peekListRef}>
              {peekLoading ? (
                <div className="text-center text-muted py-4"><Spinner animation="border" size="sm" /></div>
              ) : peekMessages.length === 0 ? (
                <div className="text-center text-muted py-4">Mesaj yok.</div>
              ) : peekMessages.map((item) => {
                const mine = item.senderId === peek.botId;
                const text = item.type === 'image' ? 'Fotoğraf' : item.type === 'video' ? 'Video' : item.type === 'audio' ? 'Ses' : (item.message || '');
                return (
                  <div key={item.id} className={`brf-bubble ${mine ? 'me' : 'them'}`}>{text}</div>
                );
              })}
            </div>
            {peekError && <div className="text-danger small px-3">{peekError}</div>}
            <form
              className="brf-peek-form"
              onSubmit={(e) => {
                e.preventDefault();
                sendPeek();
              }}
            >
              <Form.Control
                value={peekText}
                placeholder="Cevabı yaz"
                onChange={(e) => setPeekText(e.target.value)}
                disabled={peekSending}
              />
              <Button type="submit" variant="primary" disabled={peekSending || !peekText.trim()}>
                {peekSending ? <Spinner animation="border" size="sm" /> : 'Gönder'}
              </Button>
            </form>
          </aside>
        )}
      </div>
      <div className="brf-foot">
        <div className="brf-preview">
          {progress
            ? `${progress.current}/${progress.total} gönderiliyor · ${progress.name}`
            : result || `${selectedRows.length} sohbete cevap gidecek`}
        </div>
        <div className="d-flex gap-2">
          {sending ? (
            <Button variant="outline-danger" onClick={() => { cancelRef.current = true; }}>Durdur</Button>
          ) : (
            <Button variant="light" onClick={onHide}>İptal</Button>
          )}
          <Button
            variant="primary"
            disabled={sending || selectedRows.length === 0 || templates.length === 0}
            onClick={send}
          >
            {sending ? <Spinner animation="border" size="sm" className="me-2" /> : <FontAwesomeIcon icon={faPaperPlane} className="me-2" />}
            {sending
              ? 'Gönderiliyor...'
              : `${selectedRows.length} sohbete cevap gönder`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
